import { useState, useEffect, useRef, useCallback } from "react"
import { Button } from "../ui/button"
import { IoFilterOutline } from "react-icons/io5"
import { IoMdPricetag } from "react-icons/io"
import { IoClose } from "react-icons/io5"
import { cn } from "@/lib/utils"
import { ButtonSelect } from "../ButtonSelect"
import { useSavedViews } from "@/contexts/SavedViewsContext"
import { Separator } from "../ui/separator"
import { useMetadataKeys, useMetadataKeyValues, useCachedMetadataKeys, useCachedInitialValues } from "@/data/hooks/api/useMetadata"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { LuCheck, LuChevronDown } from "react-icons/lu"

export interface AppliedFilter {
  id: string
  tagName: string
  tagNameLabel: string
  operation: string
  operationLabel: string
  value: string
  isNew?: boolean // Flag for newly added filters that need auto-focus
}

const DEFAULT_OPERATION_OPTIONS = [
  { value: "is", label: "is" },
  { value: "is_not", label: "is not" },
]

// Searchable, paginated value select component
interface SearchableValueSelectProps {
  keyName: string | null
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
  size?: "xs" | "sm" | "default"
  autoFocus?: boolean
  header?: string
  dropdownWidth?: number | string
}

const SearchableValueSelect = ({
  keyName,
  value,
  onValueChange,
  placeholder = "Select value...",
  open: controlledOpen,
  onOpenChange,
  className,
  size = "xs",
  autoFocus,
  header,
  dropdownWidth = 200,
}: SearchableValueSelectProps) => {
  const [internalOpen, setInternalOpen] = useState(() => autoFocus ?? false)
  const [search, setSearch] = useState("")
  const [skip, setSkip] = useState(0)
  const [allValues, setAllValues] = useState<string[]>([])
  const listRef = useRef<HTMLDivElement>(null)
  const limit = 50

  // Get cached initial values from React Query cache
  const cachedInitialValues = useCachedInitialValues(keyName)

  // Use controlled or uncontrolled open state
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen

  const setOpen = useCallback((newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen)
    }
    onOpenChange?.(newOpen)
    if (!newOpen) {
      // Reset search and skip when closing
      setSearch("")
      setSkip(0)
      setAllValues([])
    }
  }, [isControlled, onOpenChange])

  // Only fetch from API when searching or paginating
  const isSearchingOrPaginating = search !== '' || skip > 0
  const shouldFetch = open && keyName !== null && isSearchingOrPaginating
  const { data: fetchedData, isLoading: loading } = useMetadataKeyValues(
    shouldFetch ? keyName : null,
    search,
    skip,
    limit,
    shouldFetch
  )

  // Load initial values from cache when popover opens
  useEffect(() => {
    if (open && keyName && !isSearchingOrPaginating) {
      if (cachedInitialValues) {
        setAllValues(cachedInitialValues.values)
      } else {
        // If no cached data, clear values
        setAllValues([])
      }
    }
  }, [open, keyName, isSearchingOrPaginating, cachedInitialValues])

  // Update values when fetched data changes (from search or pagination)
  useEffect(() => {
    if (fetchedData && shouldFetch) {
      if (skip === 0 && search !== '') {
        // Search results - replace all values
        setAllValues(fetchedData.values)
      } else if (skip > 0) {
        // Pagination - append values (avoid duplicates)
        setAllValues((prev) => {
          const newValues = fetchedData.values.filter(v => !prev.includes(v))
          return [...prev, ...newValues]
        })
      }
    }
  }, [fetchedData, shouldFetch, skip, search])

  // Reset when key changes
  useEffect(() => {
    if (keyName) {
      setSearch("")
      setSkip(0)
      // Don't reset allValues here - let the open effect handle it
    }
  }, [keyName])

  // Calculate hasMore: use fetchedData total if available, otherwise use cached total
  const hasMore = (() => {
    if (isSearchingOrPaginating && fetchedData) {
      return skip + limit < fetchedData.total
    }
    if (!isSearchingOrPaginating && cachedInitialValues) {
      // For initial values, we only have the first 10, so check if there are more
      return cachedInitialValues.values.length < cachedInitialValues.total
    }
    return false
  })()

  // Load more values on scroll
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget
      if (!hasMore || loading) return

      const isNearBottom =
        target.scrollHeight - target.scrollTop <= target.clientHeight * 1.5

      if (isNearBottom) {
        // If we're on initial values and need to load more, start fetching from API
        if (!isSearchingOrPaginating && cachedInitialValues) {
          if (cachedInitialValues.values.length < cachedInitialValues.total) {
            // Start fetching from API with skip = initial values length
            setSkip(cachedInitialValues.values.length)
          }
        } else {
          // Continue pagination
          setSkip((prev) => prev + limit)
        }
      }
    },
    [hasMore, loading, limit, isSearchingOrPaginating, cachedInitialValues]
  )

  // Reset skip when search changes
  useEffect(() => {
    if (open && search !== undefined) {
      setSkip(0)
      // If search is cleared, reload initial values from cache
      if (search === '' && cachedInitialValues) {
        setAllValues(cachedInitialValues.values)
      } else if (search === '') {
        setAllValues([])
      }
    }
  }, [search, open, cachedInitialValues])

  const handleSelect = (val: string) => {
    onValueChange(val)
    setOpen(false)
  }

  const displayValue = value || placeholder

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="transparent-outline"
          size={size}
          className={cn(
            "hover:bg-[#F2EEFF] hover:text-[#654EDA] hover:border-[#E2DAFF]",
            "focus-visible:ring-[#654EDA]/20 focus-visible:border-[#654EDA] focus-visible:ring-2",
            open && "bg-[#E2DAFF] text-[#654EDA] border-[#C6B9FF] hover:bg-[#E2DAFF] focus-visible:ring-0",
            "border-0 h-full px-2 font-medium",
            !value && "text-[#8A8F98] font-normal",
            className
          )}
          role="combobox"
          aria-expanded={open}
        >
          <span className="whitespace-nowrap">{displayValue}</span>
          <LuChevronDown
            className={cn(
              "size-3.5 ml-0.5 transition-transform duration-200 shrink-0",
              open && "rotate-180"
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 border-[#EDEEF3] shadow-lg flex flex-col max-h-[var(--radix-popover-content-available-height,400px)]"
        style={{ width: typeof dropdownWidth === 'number' ? `${dropdownWidth}px` : dropdownWidth }}
        align="start"
        sideOffset={6}
      >
        {/* Header */}
        {header && (
          <div className="px-3 py-2 border-b border-[#EDEEF3] shrink-0">
            <span className="text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide">
              {header}
            </span>
          </div>
        )}

        <Command shouldFilter={false} className="flex-1 min-h-0 flex flex-col">
          <CommandInput
            placeholder="Search values..."
            value={search}
            onValueChange={setSearch}
            className="h-9 shrink-0"
          />
          <CommandList
            ref={listRef}
            onScroll={handleScroll}
            onWheel={(e) => e.stopPropagation()}
            className="flex-1 min-h-0 max-h-[280px] overflow-y-auto"
          >
            <CommandEmpty>
              {loading ? "Loading..." : "No values found."}
            </CommandEmpty>
            <CommandGroup>
              {allValues.map((val) => {
                const isSelected = value === val
                return (
                  <CommandItem
                    key={val}
                    value={val}
                    onSelect={() => handleSelect(val)}
                    className={cn(
                      "flex items-center gap-3 px-3 min-h-[32px] cursor-pointer",
                      isSelected && "bg-[#F7F8F8]"
                    )}
                  >
                    <span className={cn(
                      "text-[13px] font-medium flex-1 break-words",
                      isSelected ? "text-[#654EDA]" : "text-[#08090A]"
                    )}>
                      {val}
                    </span>
                    {isSelected && (
                      <LuCheck className="size-4 text-[#654EDA] shrink-0" />
                    )}
                  </CommandItem>
                )
              })}
              {loading && (
                <div className="py-2 text-center text-sm text-[#8A8F98]">
                  Loading more...
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface FiltersSelectorProps {
  /** Callback when user clicks Clear button - use this to clear models or other filters in parent */
  onClearAll?: () => void
  /** Whether to hide the reset filters button (when it's rendered elsewhere) */
  hideResetButton?: boolean
}

const FiltersSelector = ({ onClearAll, hideResetButton }: FiltersSelectorProps = {}) => {
  const { currentFilters, setCurrentFilters } = useSavedViews()

  // Fetch metadata keys from API
  const { data: metadataKeysData } = useMetadataKeys()

  // Get cached keys from React Query
  const cachedKeys = useCachedMetadataKeys()

  // Convert metadata keys to options format, combining API data with cached keys
  const tagNameOptions = (() => {
    const apiKeys = metadataKeysData?.keys?.map((key) => key.key) || []
    const allKeys = [...new Set([...apiKeys, ...cachedKeys])]

    return allKeys.map((key) => ({
      value: key,
      label: key,
    }))
  })()

  const operationOptions = [
    { value: "is", label: "is" },
    { value: "is_not", label: "is not" },
  ]

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([])
  const [pendingFilterId, setPendingFilterId] = useState<string | null>(null)

  // Track if we're updating from context to prevent circular updates
  const isUpdatingFromContext = useRef(false)
  const lastContextFiltersRef = useRef<string>("")

  // Sync with SavedViews context when a saved view is applied (from external source)
  useEffect(() => {
    const contextFiltersJson = JSON.stringify(currentFilters)

    // Only sync if context changed externally (not from our own update)
    if (contextFiltersJson !== lastContextFiltersRef.current) {
      isUpdatingFromContext.current = true
      lastContextFiltersRef.current = contextFiltersJson
      setAppliedFilters(currentFilters)
      setPendingFilterId(null)
      // Reset flag after state update
      requestAnimationFrame(() => {
        isUpdatingFromContext.current = false
      })
    }
  }, [currentFilters])

  // Update context when filters change (but not when syncing from context)
  useEffect(() => {
    if (isUpdatingFromContext.current) return

    const filtersWithoutNew = appliedFilters.filter(f => !f.isNew).map(({ isNew, ...rest }) => rest)
    const filtersJson = JSON.stringify(filtersWithoutNew)

    // Only update if actually different
    if (filtersJson !== lastContextFiltersRef.current) {
      lastContextFiltersRef.current = filtersJson
      setCurrentFilters(filtersWithoutNew)
    }
  }, [appliedFilters, setCurrentFilters])

  const handleAddFilter = useCallback(() => {
    const newId = Date.now().toString()
    const newFilter: AppliedFilter = {
      id: newId,
      tagName: "",
      tagNameLabel: "",
      operation: "is",
      operationLabel: "is",
      value: "",
      isNew: true,
    }
    setAppliedFilters(prev => [...prev, newFilter])
    setPendingFilterId(newId)
  }, [])

  const handleRemoveFilter = useCallback((id: string) => {
    setAppliedFilters(prev => prev.filter(filter => filter.id !== id))
    if (pendingFilterId === id) {
      setPendingFilterId(null)
    }
  }, [pendingFilterId])

  const handleUpdateFilter = useCallback((id: string, updates: Partial<AppliedFilter>) => {
    setAppliedFilters(prev =>
      prev.map(filter => {
        if (filter.id !== id) return filter

        const updatedFilter = { ...filter, ...updates }

        // Update labels when values change
        if (updates.tagName !== undefined) {
          const tagOption = tagNameOptions.find(opt => opt.value === updates.tagName)
          updatedFilter.tagNameLabel = tagOption?.label || updates.tagName
          // Reset value when tag changes
          if (filter.tagName !== updates.tagName) {
            updatedFilter.value = ""
          }
        }

        if (updates.operation !== undefined) {
          const opOption = operationOptions.find(opt => opt.value === updates.operation)
          updatedFilter.operationLabel = opOption?.label || updates.operation
        }

        // Remove isNew flag when filter is complete
        if (updatedFilter.tagName && updatedFilter.operation && updatedFilter.value) {
          updatedFilter.isNew = false
        }

        return updatedFilter
      })
    )
  }, [])

  const handleClearAll = useCallback(() => {
    // Clear local state
    setAppliedFilters([])
    setPendingFilterId(null)
    // Clear context filters directly (don't rely on effect)
    lastContextFiltersRef.current = "[]"
    setCurrentFilters([])
    // Call parent callback to clear models and other filters
    if (onClearAll) {
      onClearAll()
    }
  }, [onClearAll, setCurrentFilters])

  // Determine if a filter is being edited (new, pending, or incomplete)
  const isFilterBeingEdited = (f: AppliedFilter) =>
    f.isNew || pendingFilterId === f.id || (f.tagName && !f.value)

  // Check if any filter is currently being edited (for showing/hiding add button)
  const hasEditingFilter = appliedFilters.some(isFilterBeingEdited)

  // Count complete filters for showing clear button
  const completeFiltersCount = appliedFilters.filter(
    f => f.tagName && f.operation && f.value && !f.isNew
  ).length

  return (
    <div className="flex items-start gap-2 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
        {/* Render all filters in order, using appropriate component */}
        {appliedFilters.map(filter => {
          const isEditing = isFilterBeingEdited(filter)

          if (isEditing) {
            return (
              <InlineFilterEditor
                key={filter.id}
                filter={filter}
                tagNameOptions={tagNameOptions}
                operationOptions={operationOptions}
                autoFocusTagName={filter.isNew}
                autoFocusValue={!!filter.tagName && !filter.value}
                onUpdate={(updates) => handleUpdateFilter(filter.id, updates)}
                onRemove={() => handleRemoveFilter(filter.id)}
                onComplete={() => setPendingFilterId(null)}
              />
            )
          }

          return (
            <InlineFilterChip
              key={filter.id}
              filter={filter}
              tagNameOptions={tagNameOptions}
              operationOptions={operationOptions}
              onUpdate={(updates) => handleUpdateFilter(filter.id, updates)}
              onRemove={() => handleRemoveFilter(filter.id)}
              onStartEditing={() => setPendingFilterId(filter.id)}
            />
          )
        })}

        {/* Add filter button */}
        {!hasEditingFilter && (
          <Button
            size="xs"
            variant="ghost"
            className={cn(
              "text-[#08090A] shrink-0",
              "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
              "focus-visible:ring-[#654EDA]/20 focus-visible:ring-2 focus-visible:bg-[#F2EEFF]"
            )}
            onClick={handleAddFilter}
          >
            <IoFilterOutline />
            {appliedFilters.length === 0 && "Filters"}
          </Button>
        )}
      </div>

      {/* Clear all button */}
      {completeFiltersCount > 0 && !hideResetButton && (
        <div className="flex items-center gap-2 shrink-0">
          <div className="h-[30px] w-px bg-[lch(83.025_0_282.863)]" />
          <Button
            size="xs"
            variant="ghost"
            onClick={handleClearAll}
          >
            Reset Filters
          </Button>
        </div>
      )}
    </div>
  )
}

// Component for displaying a completed filter as a chip
interface InlineFilterChipProps {
  filter: AppliedFilter
  tagNameOptions: Array<{ value: string; label: string }>
  operationOptions: Array<{ value: string; label: string }>
  onUpdate: (updates: Partial<AppliedFilter>) => void
  onRemove: () => void
  onStartEditing: () => void
}

const InlineFilterChip = ({
  filter,
  tagNameOptions,
  operationOptions,
  onUpdate,
  onRemove,
  onStartEditing,
}: InlineFilterChipProps) => {
  const [editingField, setEditingField] = useState<"tagName" | "operation" | "value" | null>(null)

  const hoverStyles = "hover:text-[#654EDA] hover:bg-[#F2EEFF] transition-all cursor-pointer"

  return (
    <div className="inline-flex items-center gap-0 h-[30px] rounded-[5px] border-[0.5px] border-[lch(83.025_0_282.863)] bg-white text-[lch(38.893_1_282.863)] text-xs whitespace-nowrap w-fit min-w-0">
      {/* Tag Name */}
      <ButtonSelect
        value={filter.tagName}
        onValueChange={(value) => {
          // When tag changes, put filter into editing mode to select new value
          onUpdate({ tagName: value })
          onStartEditing()
          setEditingField(null)
        }}
        options={tagNameOptions}
        placeholder="tag"
        icon={<IoMdPricetag className="size-3.5" />}
        size="xs"
        open={editingField === "tagName"}
        onOpenChange={(open) => setEditingField(open ? "tagName" : null)}
        className={cn(
          "border-0 rounded-l-[5px] rounded-r-none h-full px-2 shrink-0",
          "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
          "focus-visible:ring-0 focus-visible:bg-[#F2EEFF]",
          editingField === "tagName" && "bg-[#F2EEFF] text-[#654EDA]"
        )}
        dropdownWidth={160}
        dropdownClassName="max-h-[240px]"
      />

      <Separator orientation="vertical" className="h-4 bg-[lch(83.025_0_282.863)] shrink-0" />

      {/* Operation */}
      <ButtonSelect
        value={filter.operation}
        onValueChange={(value) => {
          onUpdate({ operation: value })
          setEditingField(null)
        }}
        options={operationOptions}
        placeholder="op"
        size="xs"
        open={editingField === "operation"}
        onOpenChange={(open) => setEditingField(open ? "operation" : null)}
        className={cn(
          "border-0 rounded-none h-full px-2 shrink-0 text-[lch(60_0_282.863)]",
          "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
          "focus-visible:ring-0 focus-visible:bg-[#F2EEFF]",
          editingField === "operation" && "bg-[#F2EEFF] text-[#654EDA]"
        )}
        dropdownWidth={120}
        dropdownClassName="max-h-[240px]"
      />

      <Separator orientation="vertical" className="h-4 bg-[lch(83.025_0_282.863)] shrink-0" />

      {/* Value - using searchable select */}
      <SearchableValueSelect
        keyName={filter.tagName || null}
        value={filter.value}
        onValueChange={(value) => {
          onUpdate({ value })
          setEditingField(null)
        }}
        placeholder="value"
        size="xs"
        open={editingField === "value"}
        onOpenChange={(open) => setEditingField(open ? "value" : null)}
        className={cn(
          "border-0 rounded-none h-full font-medium",
          "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
          "focus-visible:ring-0 focus-visible:bg-[#F2EEFF]",
          editingField === "value" && "bg-[#F2EEFF] text-[#654EDA]"
        )}
        dropdownWidth={250}
        header={filter.tagNameLabel || "Select value"}
      />

      <Separator orientation="vertical" className="h-4 bg-[lch(83.025_0_282.863)] shrink-0" />

      {/* Remove button */}
      <div
        className={cn(
          "inline-flex items-center justify-center shrink-0 min-w-[15px] px-2 h-full rounded-r-[5px] rounded-l-none",
          hoverStyles
        )}
        onClick={onRemove}
      >
        <IoClose className="size-3.5 text-[lch(38.893_1_282.863)]" />
      </div>
    </div>
  )
}

// Component for editing a new/incomplete filter inline
interface InlineFilterEditorProps {
  filter: AppliedFilter
  tagNameOptions: Array<{ value: string; label: string }>
  operationOptions: Array<{ value: string; label: string }>
  autoFocusTagName?: boolean
  autoFocusValue?: boolean
  onUpdate: (updates: Partial<AppliedFilter>) => void
  onRemove: () => void
  onComplete: () => void
}

const InlineFilterEditor = ({
  filter,
  tagNameOptions,
  operationOptions,
  autoFocusTagName,
  autoFocusValue,
  onUpdate,
  onRemove,
  onComplete,
}: InlineFilterEditorProps) => {
  // Track which field should auto-open next
  const getInitialAutoOpen = () => {
    if (autoFocusTagName) return "tagName"
    if (autoFocusValue) return "value"
    return null
  }
  const [pendingAutoOpen, setPendingAutoOpen] = useState<"tagName" | "value" | null>(getInitialAutoOpen)
  const [valueSelectOpen, setValueSelectOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Auto-progress to next field
  const handleTagNameChange = useCallback((value: string) => {
    onUpdate({ tagName: value })
    // Queue auto-open for value field after state updates
    setPendingAutoOpen("value")
    setValueSelectOpen(true)
  }, [onUpdate])

  const handleOperationChange = useCallback((value: string) => {
    onUpdate({ operation: value })
    if (!filter.value) {
      setPendingAutoOpen("value")
      setValueSelectOpen(true)
    }
  }, [onUpdate, filter.value])

  const handleValueChange = useCallback((value: string) => {
    onUpdate({ value })
    setPendingAutoOpen(null)
    // Check if filter is complete
    if (filter.tagName && filter.operation) {
      onComplete()
    }
  }, [onUpdate, filter.tagName, filter.operation, onComplete])

  const isComplete = filter.tagName && filter.operation && filter.value

  // Determine auto-focus for each field
  const tagNameAutoFocus = pendingAutoOpen === "tagName"
  const valueAutoFocus = pendingAutoOpen === "value" && !!filter.tagName

  // Auto-open value select when pending
  useEffect(() => {
    if (valueAutoFocus && filter.tagName) {
      setValueSelectOpen(true)
    }
  }, [valueAutoFocus, filter.tagName])

  return (
    <div
      ref={containerRef}
      className={cn(
        "inline-flex items-center gap-0 h-[30px] rounded-[5px] border-[0.5px] bg-white text-[lch(38.893_1_282.863)] text-xs whitespace-nowrap w-fit min-w-0",
        isComplete ? "border-[lch(83.025_0_282.863)]" : "border-[#654EDA] shadow-[0_0_0_2px_rgba(101,78,218,0.1)]"
      )}
    >
      {/* Tag Name */}
      <ButtonSelect
        value={filter.tagName}
        onValueChange={handleTagNameChange}
        options={tagNameOptions}
        placeholder="Select tag..."
        icon={<IoMdPricetag className="size-3.5" />}
        size="xs"
        autoFocus={tagNameAutoFocus}
        onOpenChange={(open) => {
          if (!open && pendingAutoOpen === "tagName") {
            setPendingAutoOpen(null)
          }
        }}
        className={cn(
          "border-0 rounded-l-[5px] rounded-r-none h-full px-2 shrink-0",
          "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
          "focus-visible:ring-0 focus-visible:bg-[#F2EEFF]",
          !filter.tagName && "text-[#8A8F98]"
        )}
        dropdownWidth={180}
        header="Select tag name"
      />

      <Separator orientation="vertical" className="h-4 bg-[lch(83.025_0_282.863)] shrink-0" />

      {/* Operation - always visible */}
      <ButtonSelect
        value={filter.operation}
        onValueChange={handleOperationChange}
        options={operationOptions}
        placeholder="is"
        size="xs"
        className={cn(
          "border-0 rounded-none h-full px-2 shrink-0 text-[lch(60_0_282.863)]",
          "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
          "focus-visible:ring-0 focus-visible:bg-[#F2EEFF]"
        )}
        dropdownWidth={120}
        header="Operation"
      />

      <Separator orientation="vertical" className="h-4 bg-[lch(83.025_0_282.863)] shrink-0" />

      {/* Value - show placeholder or selector based on tag selection */}
      {filter.tagName ? (
        <SearchableValueSelect
          key={`value-${filter.tagName}`}
          keyName={filter.tagName}
          value={filter.value}
          onValueChange={handleValueChange}
          placeholder="Select value..."
          size="xs"
          open={valueSelectOpen}
          onOpenChange={(open) => {
            setValueSelectOpen(open)
            if (!open && pendingAutoOpen === "value") {
              setPendingAutoOpen(null)
            }
          }}
          className={cn(
            "border-0 rounded-none h-full font-medium",
            "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
            "focus-visible:ring-0 focus-visible:bg-[#F2EEFF]",
            !filter.value && "text-[#8A8F98] font-normal"
          )}
          dropdownWidth={250}
          header={`Select ${filter.tagNameLabel || "value"}`}
        />
      ) : (
        <div className="inline-flex items-center justify-center shrink-0 px-2 h-full text-[#8A8F98] rounded-none">
          Select tag first...
        </div>
      )}

      <Separator orientation="vertical" className="h-4 bg-[lch(83.025_0_282.863)] shrink-0" />

      {/* Remove/Cancel button */}
      <div
        className={cn(
          "inline-flex items-center justify-center shrink-0 min-w-[15px] px-2 h-full rounded-r-[5px] rounded-l-none",
          "hover:text-[#654EDA] hover:bg-[#F2EEFF] transition-all cursor-pointer"
        )}
        onClick={onRemove}
      >
        <IoClose className="size-3.5 text-[lch(38.893_1_282.863)]" />
      </div>
    </div>
  )
}

// Standalone version for use in forms/modals (no context dependency)
interface InlineMetadataFiltersProps {
  value: AppliedFilter[]
  onChange: (filters: AppliedFilter[]) => void
  /** Optional override for tag name options. If not provided, fetches from API. */
  tagNameOptions?: Array<{ value: string; label: string }>
  /** Optional override for operation options. Defaults to "is" / "is not". */
  operationOptions?: Array<{ value: string; label: string }>
  /** @deprecated No longer used - values are fetched from API dynamically */
  tagValueOptionsMap?: Record<string, Array<{ value: string; label: string }>>
  addButtonLabel?: string
}

const InlineMetadataFilters = ({
  value,
  onChange,
  tagNameOptions: tagNameOptionsProp,
  operationOptions = DEFAULT_OPERATION_OPTIONS,
  tagValueOptionsMap: _tagValueOptionsMap,
  addButtonLabel = "Filters",
}: InlineMetadataFiltersProps) => {
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>(value)
  const [pendingFilterId, setPendingFilterId] = useState<string | null>(null)

  // Fetch metadata keys from API (same as FiltersSelector)
  const { data: metadataKeysData } = useMetadataKeys()

  // Get cached keys from React Query
  const cachedKeys = useCachedMetadataKeys()

  // Convert metadata keys to options format, combining API data with cached keys
  // Use prop override if provided, otherwise fetch from API
  const tagNameOptions = tagNameOptionsProp ?? (() => {
    const apiKeys = metadataKeysData?.keys?.map((key) => key.key) || []
    const allKeys = [...new Set([...apiKeys, ...cachedKeys])]

    return allKeys.map((key) => ({
      value: key,
      label: key,
    }))
  })()

  // Track what we last sent to parent to avoid circular updates
  const lastSentFiltersRef = useRef<string>(JSON.stringify(value))

  // Sync internal state with external value (only for external changes)
  useEffect(() => {
    const valueJson = JSON.stringify(value)
    // Only sync if value changed from an external source (not from our own update)
    if (valueJson !== lastSentFiltersRef.current) {
      lastSentFiltersRef.current = valueJson
      setAppliedFilters(value)
    }
  }, [value])

  // Notify parent of changes
  const updateFilters = useCallback((newFilters: AppliedFilter[]) => {
    setAppliedFilters(newFilters)
    // Only send complete filters to parent (without isNew flag)
    const completeFilters = newFilters
      .filter(f => f.tagName && f.operation && f.value && !f.isNew)
      .map(({ isNew, ...rest }) => rest)
    // Track what we're sending to avoid re-syncing it
    lastSentFiltersRef.current = JSON.stringify(completeFilters)
    onChange(completeFilters)
  }, [onChange])

  const handleAddFilter = useCallback(() => {
    const newId = Date.now().toString()
    const newFilter: AppliedFilter = {
      id: newId,
      tagName: "",
      tagNameLabel: "",
      operation: "is",
      operationLabel: "is",
      value: "",
      isNew: true,
    }
    setAppliedFilters(prev => [...prev, newFilter])
    setPendingFilterId(newId)
  }, [])

  const handleRemoveFilter = useCallback((id: string) => {
    const newFilters = appliedFilters.filter(filter => filter.id !== id)
    updateFilters(newFilters)
    if (pendingFilterId === id) {
      setPendingFilterId(null)
    }
  }, [appliedFilters, updateFilters, pendingFilterId])

  const handleUpdateFilter = useCallback((id: string, updates: Partial<AppliedFilter>) => {
    const newFilters = appliedFilters.map(filter => {
      if (filter.id !== id) return filter

      const updatedFilter = { ...filter, ...updates }

      // Update labels when values change
      if (updates.tagName !== undefined) {
        const tagOption = tagNameOptions.find(opt => opt.value === updates.tagName)
        updatedFilter.tagNameLabel = tagOption?.label || updates.tagName
        // Reset value when tag changes
        if (filter.tagName !== updates.tagName) {
          updatedFilter.value = ""
        }
      }

      if (updates.operation !== undefined) {
        const opOption = operationOptions.find(opt => opt.value === updates.operation)
        updatedFilter.operationLabel = opOption?.label || updates.operation
      }

      // Remove isNew flag when filter is complete
      if (updatedFilter.tagName && updatedFilter.operation && updatedFilter.value) {
        updatedFilter.isNew = false
      }

      return updatedFilter
    })
    updateFilters(newFilters)
  }, [appliedFilters, tagNameOptions, operationOptions, updateFilters])

  // Determine if a filter is being edited (new, pending, or incomplete)
  const isFilterBeingEdited = (f: AppliedFilter) =>
    f.isNew || pendingFilterId === f.id || (f.tagName && !f.value)

  // Check if any filter is currently being edited (for showing/hiding add button)
  const hasEditingFilter = appliedFilters.some(isFilterBeingEdited)

  return (
    <div className="flex items-start gap-2 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
        {/* Render all filters in order, using appropriate component */}
        {appliedFilters.map(filter => {
          const isEditing = isFilterBeingEdited(filter)

          if (isEditing) {
            return (
              <StandaloneInlineFilterEditor
                key={filter.id}
                filter={filter}
                tagNameOptions={tagNameOptions}
                operationOptions={operationOptions}
                autoFocusTagName={filter.isNew}
                autoFocusValue={!!filter.tagName && !filter.value}
                onUpdate={(updates) => handleUpdateFilter(filter.id, updates)}
                onRemove={() => handleRemoveFilter(filter.id)}
                onComplete={() => setPendingFilterId(null)}
              />
            )
          }

          return (
            <StandaloneInlineFilterChip
              key={filter.id}
              filter={filter}
              tagNameOptions={tagNameOptions}
              operationOptions={operationOptions}
              onUpdate={(updates) => handleUpdateFilter(filter.id, updates)}
              onRemove={() => handleRemoveFilter(filter.id)}
              onStartEditing={() => setPendingFilterId(filter.id)}
            />
          )
        })}

        {/* Add filter button */}
        {!hasEditingFilter && (
          <Button
            type="button"
            size="xs"
            variant="ghost"
            className={cn(
              "text-[#08090A] shrink-0",
              "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
              "focus-visible:ring-[#654EDA]/20 focus-visible:ring-2 focus-visible:bg-[#F2EEFF]"
            )}
            onClick={handleAddFilter}
          >
            <IoFilterOutline />
            {appliedFilters.length === 0 && addButtonLabel}
          </Button>
        )}
      </div>
    </div>
  )
}

// Standalone inline filter chip (no context dependency)
interface StandaloneInlineFilterChipProps {
  filter: AppliedFilter
  tagNameOptions: Array<{ value: string; label: string }>
  operationOptions: Array<{ value: string; label: string }>
  onUpdate: (updates: Partial<AppliedFilter>) => void
  onRemove: () => void
  onStartEditing: () => void
}

const StandaloneInlineFilterChip = ({
  filter,
  tagNameOptions,
  operationOptions,
  onUpdate,
  onRemove,
  onStartEditing,
}: StandaloneInlineFilterChipProps) => {
  const [editingField, setEditingField] = useState<"tagName" | "operation" | "value" | null>(null)

  const hoverStyles = "hover:text-[#654EDA] hover:bg-[#F2EEFF] transition-all cursor-pointer"

  return (
    <div className="inline-flex items-center gap-0 h-[30px] rounded-[5px] border-[0.5px] border-[lch(83.025_0_282.863)] bg-white text-[lch(38.893_1_282.863)] text-xs whitespace-nowrap w-fit min-w-0">
      {/* Tag Name */}
      <ButtonSelect
        value={filter.tagName}
        onValueChange={(value) => {
          // When tag changes, put filter into editing mode to select new value
          onUpdate({ tagName: value })
          onStartEditing()
          setEditingField(null)
        }}
        options={tagNameOptions}
        placeholder="tag"
        icon={<IoMdPricetag className="size-3.5" />}
        size="xs"
        open={editingField === "tagName"}
        onOpenChange={(open) => setEditingField(open ? "tagName" : null)}
        className={cn(
          "border-0 rounded-l-[5px] rounded-r-none h-full px-2 shrink-0",
          "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
          "focus-visible:ring-0 focus-visible:bg-[#F2EEFF]",
          editingField === "tagName" && "bg-[#F2EEFF] text-[#654EDA]"
        )}
        dropdownWidth={160}
        dropdownClassName="max-h-[240px]"
      />

      <Separator orientation="vertical" className="h-4 bg-[lch(83.025_0_282.863)] shrink-0" />

      {/* Operation */}
      <ButtonSelect
        value={filter.operation}
        onValueChange={(value) => {
          onUpdate({ operation: value })
          setEditingField(null)
        }}
        options={operationOptions}
        placeholder="op"
        size="xs"
        open={editingField === "operation"}
        onOpenChange={(open) => setEditingField(open ? "operation" : null)}
        className={cn(
          "border-0 rounded-none h-full px-2 shrink-0 text-[lch(60_0_282.863)]",
          "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
          "focus-visible:ring-0 focus-visible:bg-[#F2EEFF]",
          editingField === "operation" && "bg-[#F2EEFF] text-[#654EDA]"
        )}
        dropdownWidth={120}
      />

      <Separator orientation="vertical" className="h-4 bg-[lch(83.025_0_282.863)] shrink-0" />

      {/* Value - using searchable select */}
      <SearchableValueSelect
        keyName={filter.tagName || null}
        value={filter.value}
        onValueChange={(value) => {
          onUpdate({ value })
          setEditingField(null)
        }}
        placeholder="value"
        size="xs"
        open={editingField === "value"}
        onOpenChange={(open) => setEditingField(open ? "value" : null)}
        className={cn(
          "border-0 rounded-none h-full font-medium",
          "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
          "focus-visible:ring-0 focus-visible:bg-[#F2EEFF]",
          editingField === "value" && "bg-[#F2EEFF] text-[#654EDA]"
        )}
        dropdownWidth={250}
        header={filter.tagNameLabel || "Select value"}
      />

      <Separator orientation="vertical" className="h-4 bg-[lch(83.025_0_282.863)] shrink-0" />

      {/* Remove button */}
      <div
        className={cn(
          "inline-flex items-center justify-center shrink-0 min-w-[15px] px-2 h-full rounded-r-[5px] rounded-l-none",
          hoverStyles
        )}
        onClick={onRemove}
      >
        <IoClose className="size-3.5 text-[lch(38.893_1_282.863)]" />
      </div>
    </div>
  )
}

// Standalone inline filter editor (no context dependency)
interface StandaloneInlineFilterEditorProps {
  filter: AppliedFilter
  tagNameOptions: Array<{ value: string; label: string }>
  operationOptions: Array<{ value: string; label: string }>
  autoFocusTagName?: boolean
  autoFocusValue?: boolean
  onUpdate: (updates: Partial<AppliedFilter>) => void
  onRemove: () => void
  onComplete: () => void
}

const StandaloneInlineFilterEditor = ({
  filter,
  tagNameOptions,
  operationOptions,
  autoFocusTagName,
  autoFocusValue,
  onUpdate,
  onRemove,
  onComplete,
}: StandaloneInlineFilterEditorProps) => {
  // Track which field should auto-open next
  const getInitialAutoOpen = () => {
    if (autoFocusTagName) return "tagName"
    if (autoFocusValue) return "value"
    return null
  }
  const [pendingAutoOpen, setPendingAutoOpen] = useState<"tagName" | "value" | null>(getInitialAutoOpen)
  const [valueSelectOpen, setValueSelectOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Auto-progress to next field
  const handleTagNameChange = useCallback((value: string) => {
    onUpdate({ tagName: value })
    // Queue auto-open for value field after state updates
    setPendingAutoOpen("value")
    setValueSelectOpen(true)
  }, [onUpdate])

  const handleOperationChange = useCallback((value: string) => {
    onUpdate({ operation: value })
    if (!filter.value) {
      setPendingAutoOpen("value")
      setValueSelectOpen(true)
    }
  }, [onUpdate, filter.value])

  const handleValueChange = useCallback((value: string) => {
    onUpdate({ value })
    setPendingAutoOpen(null)
    // Check if filter is complete
    if (filter.tagName && filter.operation) {
      onComplete()
    }
  }, [onUpdate, filter.tagName, filter.operation, onComplete])

  const isComplete = filter.tagName && filter.operation && filter.value

  // Determine auto-focus for each field
  const tagNameAutoFocus = pendingAutoOpen === "tagName"
  const valueAutoFocus = pendingAutoOpen === "value" && !!filter.tagName

  // Auto-open value select when pending
  useEffect(() => {
    if (valueAutoFocus && filter.tagName) {
      setValueSelectOpen(true)
    }
  }, [valueAutoFocus, filter.tagName])

  return (
    <div
      ref={containerRef}
      className={cn(
        "inline-flex items-center gap-0 h-[30px] rounded-[5px] border-[0.5px] bg-white text-[lch(38.893_1_282.863)] text-xs whitespace-nowrap w-fit min-w-0",
        isComplete ? "border-[lch(83.025_0_282.863)]" : "border-[#654EDA] shadow-[0_0_0_2px_rgba(101,78,218,0.1)]"
      )}
    >
      {/* Tag Name */}
      <ButtonSelect
        value={filter.tagName}
        onValueChange={handleTagNameChange}
        options={tagNameOptions}
        placeholder="Select tag..."
        icon={<IoMdPricetag className="size-3.5" />}
        size="xs"
        autoFocus={tagNameAutoFocus}
        onOpenChange={(open) => {
          if (!open && pendingAutoOpen === "tagName") {
            setPendingAutoOpen(null)
          }
        }}
        className={cn(
          "border-0 rounded-l-[5px] rounded-r-none h-full px-2 shrink-0",
          "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
          "focus-visible:ring-0 focus-visible:bg-[#F2EEFF]",
          !filter.tagName && "text-[#8A8F98]"
        )}
        dropdownWidth={180}
        header="Select tag name"
      />

      <Separator orientation="vertical" className="h-4 bg-[lch(83.025_0_282.863)] shrink-0" />

      {/* Operation - always visible */}
      <ButtonSelect
        value={filter.operation}
        onValueChange={handleOperationChange}
        options={operationOptions}
        placeholder="is"
        size="xs"
        className={cn(
          "border-0 rounded-none h-full px-2 shrink-0 text-[lch(60_0_282.863)]",
          "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
          "focus-visible:ring-0 focus-visible:bg-[#F2EEFF]"
        )}
        dropdownWidth={120}
        header="Operation"
      />

      <Separator orientation="vertical" className="h-4 bg-[lch(83.025_0_282.863)] shrink-0" />

      {/* Value - show placeholder or selector based on tag selection */}
      {filter.tagName ? (
        <SearchableValueSelect
          key={`value-${filter.tagName}`}
          keyName={filter.tagName}
          value={filter.value}
          onValueChange={handleValueChange}
          placeholder="Select value..."
          size="xs"
          open={valueSelectOpen}
          onOpenChange={(open) => {
            setValueSelectOpen(open)
            if (!open && pendingAutoOpen === "value") {
              setPendingAutoOpen(null)
            }
          }}
          className={cn(
            "border-0 rounded-none h-full font-medium",
            "hover:bg-[#F2EEFF] hover:text-[#654EDA]",
            "focus-visible:ring-0 focus-visible:bg-[#F2EEFF]",
            !filter.value && "text-[#8A8F98] font-normal"
          )}
          dropdownWidth={250}
          header={`Select ${filter.tagNameLabel || "value"}`}
        />
      ) : (
        <div className="inline-flex items-center justify-center shrink-0 px-2 h-full text-[#8A8F98] rounded-none">
          Select tag first...
        </div>
      )}

      <Separator orientation="vertical" className="h-4 bg-[lch(83.025_0_282.863)] shrink-0" />

      {/* Remove/Cancel button */}
      <div
        className={cn(
          "inline-flex items-center justify-center shrink-0 min-w-[15px] px-2 h-full rounded-r-[5px] rounded-l-none",
          "hover:text-[#654EDA] hover:bg-[#F2EEFF] transition-all cursor-pointer"
        )}
        onClick={onRemove}
      >
        <IoClose className="size-3.5 text-[lch(38.893_1_282.863)]" />
      </div>
    </div>
  )
}

export { FiltersSelector, InlineMetadataFilters }
